import { useEffect, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Button, Icon } from "semantic-ui-react";

import "./CardUploadImagePackage.scss";

type CardUploadImagePackageProps = {
  label: string;
  imageHero?: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  imageExtra?: Record<string, any>;
  onChange: (
    hero: string,
    thumbnail: string,
    file: File | null,
    crop: Record<string, any>,
    zoom: number,
  ) => void;
};

export const CardUploadImagePackage = ({
  label,
  imageHero,
  imageExtra,
  fileInputRef,
  onChange,
}: CardUploadImagePackageProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();

  useEffect(() => {
    if (imageExtra) {
      setCrop(imageExtra?.imageHeroCrop || { x: 0, y: 0 });
      setZoom(imageExtra?.imageHeroZoom || 1);
    }
  }, [imageExtra]);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);

    // อัปโหลดครั้งแรก
    // hero = รูปต้นฉบับ
    // thumbnail = รูปที่ใช้ crop
    onChange(preview, preview, file, { x: 0, y: 0 }, 1);

    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleDelete = () => {
    onChange("", "", null, { x: 0, y: 0 }, 1);

    setCrop({ x: 0, y: 0 });
    setZoom(1);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleReset = () => {
    // กลับไปใช้รูปต้นฉบับ
    onChange(imageHero ?? "", "", null, { x: 0, y: 0 }, 1);

    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSaveCrop = async () => {
    if (!imageHero || !croppedAreaPixels) return;

    const croppedImage = await getCroppedImg(imageHero, croppedAreaPixels);

    onChange(
      imageHero, // original
      croppedImage, // thumbnail
      null,
      crop,
      zoom,
    );
  };

  return (
    <div className="upload-image-package">
      <div className="upload-label">
        <label>{label}</label>
      </div>

      <input ref={fileInputRef} hidden type="file" accept="image/*" onChange={handleUpload} />

      {imageHero ? (
        <div className="crop-wrapper">
          <Cropper
            image={imageHero}
            crop={crop}
            zoom={zoom}
            aspect={16 / 9}
            cropShape="rect"
            showGrid={false}
            objectFit="vertical-cover"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
          />

          <div className="crop-tip">✥ ลากเพื่อจัดตำแหน่ง · เลื่อนซูม</div>

          <div className="crop-toolbar">
            <Button
              size="small"
              style={{
                color: "var(--primary-theme-color-dark)",
                backgroundColor: "#fff",
              }}
              icon="refresh"
              content="เปลี่ยนรูป"
              onClick={() => fileInputRef.current?.click()}
            />

            <Button
              size="small"
              style={{ backgroundColor: "#fff" }}
              icon="undo"
              title="รีเซ็ต"
              onClick={handleReset}
            />

            <Button
              size="small"
              style={{
                color: "green",
                backgroundColor: "#fff",
              }}
              icon="check"
              content="บันทึก"
              onClick={handleSaveCrop}
            />

            <Button
              size="small"
              style={{
                color: "red",
                backgroundColor: "#fff",
              }}
              icon="trash"
              title="ลบ"
              onClick={handleDelete}
            />
          </div>

          <div className="zoom-toolbar">
            <Button
              icon="minus"
              size="mini"
              disabled={zoom <= 1}
              onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
            />

            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />

            <Button
              icon="plus"
              size="mini"
              disabled={zoom >= 3}
              onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
            />

            <span>{Math.round(zoom * 100)}%</span>
          </div>
        </div>
      ) : (
        <div className="upload-box" onClick={() => fileInputRef.current?.click()}>
          <Icon name="upload" size="huge" />

          <div className="upload-title">คลิกเพื่ออัปโหลดรูป</div>

          <div className="upload-desc">PNG / JPG</div>
        </div>
      )}
    </div>
  );
};

export async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  const image = new Image();
  image.src = imageSrc;

  await new Promise((resolve) => {
    image.onload = resolve;
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return canvas.toDataURL("image/jpeg", 0.95);
}
